import 'react-big-calendar/lib/css/react-big-calendar.css'

import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { InputType } from 'config'
import { format, getDay, parse, startOfWeek } from 'date-fns'
import { enUS } from 'date-fns/locale'
import moment from 'moment'
import { useEffect, useState } from 'react'
import { Calendar as BigCalendar, dateFnsLocalizer, Event } from 'react-big-calendar'
import { getAdminConfig, setAdminConfig } from 'services'
import { Button } from 'stories/components'
import { confirm, formatDate, InputConvert } from 'utils'
import { RenderInput } from 'utils/RenderInput'

const locales = {
  'en-US': enUS,
}

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
})

export interface Holiday {
  date: string // Format: YYYY-MM-DD
  name: string
  recurring?: boolean // If true, recurs every year
}

const getHolidayInputs = (): Record<string, InputType> => ({
  name: {
    inputType: 'text',
    title: 'Holiday Name',
    placeholder: "e.g., New Year's Day",
    required: true,
  },
  date: {
    inputType: 'text',
    type: 'date',
    title: 'Date',
    required: true,
  },
  recurring: {
    inputType: 'toggle',
    title: 'Recurring (every year)',
    tooltip: 'If marked as recurring, this holiday will apply to the same month and day every year.',
  },
})

export const HolidaysConfiguration = () => {
  const [isLoading, setLoading] = useState(true)
  const [holidays, setHolidays] = useState<Holiday[]>([])
  const [currentDate, setCurrentDate] = useState(new Date())
  const [showAddModal, setShowAddModal] = useState(false)
  const [editingHoliday, setEditingHoliday] = useState<Holiday | null>(null)
  const [inputs, setInputs] = useState<Record<string, any>>(getHolidayInputs())

  useEffect(() => {
    fetchHolidays()
  }, [])

  const fetchHolidays = async () => {
    setLoading(true)
    try {
      const data = await getAdminConfig('holidays')
      setHolidays(Array.isArray(data) ? data : [])
    } catch (error) {
      console.error('Error fetching holidays:', error)
      setHolidays([])
    } finally {
      setLoading(false)
    }
  }

  const saveHolidays = async (newHolidays: Holiday[]) => {
    setLoading(true)
    try {
      await setAdminConfig('holidays', newHolidays)
      setHolidays(newHolidays)
    } catch (error) {
      console.error('Error saving holidays:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleAddHoliday = () => {
    const holidayData: Holiday = {
      name: inputs.name.value || '',
      date: inputs.date.value || moment().format('YYYY-MM-DD'),
      recurring: inputs.recurring.value || false,
    }

    if (!holidayData.name.trim()) {
      alert('Please enter a holiday name')
      return
    }

    const newHolidays = [...holidays, holidayData]
    saveHolidays(newHolidays)
    setShowAddModal(false)
    resetForm()
  }

  const handleUpdateHoliday = () => {
    if (!editingHoliday) return

    const holidayData: Holiday = {
      name: inputs.name.value || '',
      date: inputs.date.value || moment().format('YYYY-MM-DD'),
      recurring: inputs.recurring.value || false,
    }

    if (!holidayData.name.trim()) {
      alert('Please enter a holiday name')
      return
    }

    const newHolidays = holidays.map((h) =>
      h.date === editingHoliday.date && h.name === editingHoliday.name ? holidayData : h,
    )
    saveHolidays(newHolidays)
    setEditingHoliday(null)
    setShowAddModal(false)
    resetForm()
  }

  const resetForm = () => {
    const newInputs = cloneDeep(getHolidayInputs())
    newInputs.name.value = ''
    newInputs.date.value = moment().format('YYYY-MM-DD')
    newInputs.recurring.value = false
    setInputs(newInputs)
  }

  const onChange = (key: string, value: any) => {
    const newInputs = cloneDeep(inputs)
    newInputs[key].value = InputConvert(newInputs[key], value)
    newInputs[key].error = ''
    setInputs(newInputs)
  }

  const handleDeleteHoliday = async (holiday: Holiday) => {
    const content = `Are you sure you want to delete "${holiday.name}"?`
    const isContinue = await confirm(content)
    if (!isContinue) return
    const newHolidays = holidays.filter((h) => !(h.date === holiday.date && h.name === holiday.name))
    saveHolidays(newHolidays)
  }

  const openAddModal = () => {
    setEditingHoliday(null)
    resetForm()
    setShowAddModal(true)
  }

  const openEditModal = (holiday: Holiday) => {
    setEditingHoliday(holiday)
    const newInputs = cloneDeep(getHolidayInputs())
    newInputs.name.value = holiday.name
    newInputs.date.value = holiday.date
    newInputs.recurring.value = holiday.recurring || false
    setInputs(newInputs)
    setShowAddModal(true)
  }

  // Convert holidays to calendar events
  const calendarEvents: Event[] = holidays.flatMap((holiday) => {
    // Parse YYYY-MM-DD format
    const originalDate = moment(holiday.date, 'YYYY-MM-DD')

    if (!originalDate.isValid()) {
      console.error('Invalid date format for holiday:', holiday)
      return []
    }

    if (holiday.recurring) {
      // For recurring holidays, create events for multiple years
      // Generate events from 2020 to 10 years in the future
      const currentYear = new Date().getFullYear()
      const startYear = 2020
      const endYear = currentYear + 10
      const events: Event[] = []

      for (let year = startYear; year <= endYear; year++) {
        const eventDate = moment(originalDate).year(year).toDate()
        events.push({
          title: holiday.name + ' (Recurring)',
          start: eventDate,
          end: eventDate,
          resource: holiday,
        })
      }

      return events
    } else {
      // For non-recurring holidays, just create one event
      return [
        {
          title: holiday.name,
          start: originalDate.toDate(),
          end: originalDate.toDate(),
          resource: holiday,
        },
      ]
    }
  })

  const eventStyleGetter = () => {
    return {
      style: {
        backgroundColor: '#dc2626',
        borderRadius: '5px',
        opacity: 0.9,
        color: 'white',
        border: '0px',
        display: 'block',
        fontSize: '12px',
        padding: '2px 5px',
      },
    }
  }

  // Sort holidays by date for the list view
  const sortedHolidays = [...holidays].sort((a, b) => {
    const dateA = moment(a.date, 'YYYY-MM-DD')
    const dateB = moment(b.date, 'YYYY-MM-DD')
    return dateA.valueOf() - dateB.valueOf()
  })

  return (
    <div className="relative">
      <LayoutLoading show={isLoading} />

      <div className="mb-4 flex justify-between items-center">
        <h3 className="text-lg font-semibold">
          Holidays Configuration
          <span className="text-sm text-gray-500 ml-2">(Weekends are automatically non-business days)</span>
        </h3>
        <Button onClick={openAddModal} className="flex items-center gap-2">
          <span className="flex items-center gap-2">
            <PlusIcon className="w-5 h-5" />
            Add Holiday
          </span>
        </Button>
      </div>

      <div className="flex gap-6">
        {/* Calendar View */}
        <div className="flex-1" style={{ minHeight: '600px' }}>
          <BigCalendar
            localizer={localizer}
            events={calendarEvents}
            startAccessor="start"
            endAccessor="end"
            style={{ height: '600px' }}
            date={currentDate}
            onNavigate={(date: any) => setCurrentDate(date)}
            eventPropGetter={eventStyleGetter}
            popup
            views={['month']}
            defaultView="month"
            onSelectEvent={(event: any) => {
              const resource = event.resource as Holiday
              openEditModal(resource)
            }}
          />
        </div>

        {/* Holidays List */}
        <div className="w-80 flex-shrink-0">
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-semibold mb-4 text-lg">Holidays List ({holidays.length})</h4>

            <div className="space-y-2 max-h-[550px] overflow-y-auto">
              {sortedHolidays.length === 0 ? (
                <p className="text-gray-500 text-sm text-center py-4">No holidays configured</p>
              ) : (
                sortedHolidays.map((holiday, index) => {
                  return (
                    <div
                      key={index}
                      className="bg-white border border-gray-200 rounded p-3 hover:shadow-md transition-shadow"
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h5 className="font-medium text-sm">{holiday.name}</h5>
                          <p className="text-xs text-gray-600 mt-1">{formatDate(holiday.date)}</p>
                          {holiday.recurring && (
                            <span className="inline-block mt-1 px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded">
                              Recurring
                            </span>
                          )}
                        </div>
                        <div className="flex gap-1 ml-2">
                          <button
                            onClick={() => openEditModal(holiday)}
                            className="text-blue-600 hover:text-blue-800 text-xs px-2 py-1"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteHoliday(holiday)}
                            className="text-red-600 hover:text-red-800 p-1"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  )
                })
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-xl font-semibold mb-4">{editingHoliday ? 'Edit Holiday' : 'Add Holiday'}</h3>

            <div className="space-y-4">
              {Object.keys(inputs).map((key, index) => {
                const item = inputs[key]
                return (
                  <div key={index}>
                    <RenderInput input={item} Key={key} onChange={onChange} />
                  </div>
                )
              })}
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <Button
                onClick={() => {
                  setShowAddModal(false)
                  setEditingHoliday(null)
                  resetForm()
                }}
                className="bg-gray-300 hover:bg-gray-400"
              >
                Cancel
              </Button>
              <Button onClick={editingHoliday ? handleUpdateHoliday : handleAddHoliday}>
                {editingHoliday ? 'Update' : 'Add'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
